// FILE: src/Account.jsx
import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, onSnapshot, orderBy, query, limit } from "firebase/firestore";
import { createPortalLink, getCustomerContext } from "./lib/billingApi";
import { describeLedgerSource, formatLedgerQuantity } from "./utils/tokenLedgerLabels";
import Menu from "./Menu";
import VenueSettings from "./VenueSettings";
import { appAlert } from "./components/AppDialog";

function formatStatus(status) {
  if (!status || status === "none" || status === "—") return "None";
  const s = String(status);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function Account() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [billingCtx, setBillingCtx] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setChecking(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;
    const dref = doc(db, "users", user.uid);
    const unsub = onSnapshot(dref, (snap) => {
      setProfile(snap.exists() ? snap.data() : {});
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setLedgerEntries([]);
      return;
    }
    setLedgerLoading(true);
    const q = query(
      collection(db, "users", user.uid, "tokenLedger"),
      orderBy("ts", "desc"),
      limit(100)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLedgerEntries(rows);
        setLedgerLoading(false);
      },
      (err) => {
        console.warn("tokenLedger listen failed:", err);
        setLedgerLoading(false);
      }
    );
    return () => unsub();
  }, [user]);

  // Live subscription from Stripe (more reliable than stale Firestore entitlements)
  useEffect(() => {
    if (!user) {
      setBillingCtx(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ctx = await getCustomerContext();
        if (!cancelled) setBillingCtx(ctx || null);
      } catch (e) {
        console.warn("customer-context failed:", e);
        if (!cancelled) setBillingCtx(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const ent = profile?.entitlements || {};
  const nestedTokens =
    typeof ent?.quizTokens === "number" ? ent.quizTokens : Number(ent?.quizTokens) || 0;
  const dottedTokens =
    profile && Object.prototype.hasOwnProperty.call(profile, "entitlements.quizTokens")
      ? Number(profile["entitlements.quizTokens"]) || 0
      : 0;
  const tokens = Math.max(nestedTokens, dottedTokens);

  const displayName = user?.displayName || profile?.displayName || "—";
  const email = user?.email || profile?.email || "—";

  const activeSub =
    billingCtx?.subscription &&
    ["active", "trialing"].includes(billingCtx.subscription.status)
      ? billingCtx.subscription
      : null;
  const subscriptionStatus = activeSub
    ? "Active"
    : formatStatus(ent?.subscriptionStatus);
  const weeklyQuantity = activeSub
    ? (activeSub.items?.[0]?.quantity ?? 0)
    : (typeof ent?.weeklyQuantity === "number" ? ent.weeklyQuantity : 0);

  const fmt = (ts) => {
    if (!ts) return "—";
    try {
      let ms;
      if (typeof ts?.toMillis === "function") ms = ts.toMillis();
      else if (typeof ts === "number") ms = ts < 1e12 ? ts * 1000 : ts;
      else if (ts?.seconds) ms = ts.seconds * 1000;
      else return String(ts);
      const locale =
        (typeof navigator !== "undefined" && (navigator.language || navigator.languages?.[0])) ||
        undefined;
      return new Date(ms).toLocaleString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return String(ts);
    }
  };

  const ledgerWithBalances = (() => {
    let running = tokens;
    return ledgerEntries.map((row) => {
      const qty = Number(row.quantity) || 0;
      const stored =
        row.balanceAfter != null && Number.isFinite(Number(row.balanceAfter))
          ? Number(row.balanceAfter)
          : null;
      const balanceAfter = stored != null ? stored : running;
      running = balanceAfter - qty;
      return { ...row, _balanceAfter: balanceAfter };
    });
  })();

  const card = (label, value, extra) => (
    <div
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 14,
        padding: "12px 14px",
        marginBottom: 10,
      }}
    >
      <div style={{ fontWeight: 800, color: "#d6e6ff", fontSize: "0.95rem" }}>{label}</div>
      <div style={{ color: "#f5f8ff", fontSize: "1.05rem", marginTop: 4 }}>{value ?? "—"}</div>
      {extra}
    </div>
  );

  const openPortal = async () => {
    try {
      const { url } = await createPortalLink({
        uid: user.uid,
        return_url: window.location.origin + "/account",
      });
      window.location.href = url;
    } catch (e) {
      appAlert(`Couldn't open billing portal: ${e.message || e}`);
    }
  };

  const goPricing = () => {
    window.location.href = "/pricing";
  };

  if (checking) return null;

  if (!user) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#223b4c",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}
      >
        <div>
          <h2>Please sign in</h2>
          <button onClick={() => (window.location.href = "/host")}>Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        background: "#223b4c",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100vw",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ffe066",
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          ☰ Menu
        </button>
        <div style={{ marginLeft: 8, fontWeight: 900, color: "#f8faff" }}>Account</div>
      </div>

      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      <div
        style={{
          background: "rgba(32,30,48,0.58)",
          boxShadow: "0 8px 36px 8px #1116",
          borderRadius: "2.4em",
          padding: "2.2em 2.2em",
          maxWidth: 720,
          width: "94vw",
          marginTop: 24,
          marginBottom: 40,
          color: "#f8faff",
        }}
      >
        <h2 style={{ margin: "0 0 0.6em 0", fontWeight: 900, textAlign: "center" }}>
          Your Account
        </h2>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 22 }}>
          <div
            style={{
              background: "linear-gradient(135deg, rgba(255,204,102,0.90), rgba(255,150,64,0.95))",
              color: "#1d2030",
              fontWeight: 1000,
              fontSize: "2.2rem",
              padding: "12px 22px",
              borderRadius: "1.2em",
            }}
          >
            {/* Profile loads async; don't flash "0 tokens" before the real balance arrives. */}
            {profile ? `${tokens} tokens` : "… tokens"}
          </div>
          <p style={{ margin: "10px 0 0", opacity: 0.8, fontSize: "0.95rem", textAlign: "center" }}>
            1 token = 1 live quiz night
          </p>
          <button
            onClick={goPricing}
            style={{
              marginTop: 12,
              color: "#142127",
              fontWeight: 900,
              background: "linear-gradient(135deg, rgba(158,255,168,0.9), rgba(64,201,120,0.95))",
              padding: "10px 16px",
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
            }}
          >
            Buy tokens / subscribe
          </button>
        </div>

        {card(
          "Subscription",
          activeSub
            ? `Active · ${weeklyQuantity} quiz${weeklyQuantity === 1 ? "" : "zes"} / week`
            : subscriptionStatus === "None"
              ? "No weekly plan — buy a one-off or subscribe on Pricing"
              : subscriptionStatus,
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10, gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={goPricing}
              style={{
                padding: "8px 14px",
                background: "rgba(255,255,255,0.10)",
                color: "#f8faff",
                fontWeight: 800,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.22)",
                cursor: "pointer",
              }}
            >
              Change plan
            </button>
            <button
              onClick={openPortal}
              style={{
                padding: "8px 14px",
                background: "linear-gradient(135deg, rgba(158,255,168,0.9), rgba(64,201,120,0.95))",
                color: "#142127",
                fontWeight: 900,
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
              }}
            >
              Manage billing
            </button>
          </div>
        )}

        <VenueSettings />

        {card("Name", displayName)}
        {card("Email", email)}
        {profile?.role ? card("Role", profile.role) : null}
        {card("Member since", fmt(profile?.createdAt))}

        {/* Token history — bottom, collapsed by default */}
        <div
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 14,
            padding: "12px 14px",
            marginTop: 8,
            marginBottom: 0,
          }}
        >
          <button
            type="button"
            onClick={() => setLedgerOpen((v) => !v)}
            aria-expanded={ledgerOpen}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              background: "transparent",
              border: "none",
              color: "#f8faff",
              cursor: "pointer",
              padding: 0,
              textAlign: "left",
            }}
          >
            <div>
              <div style={{ fontWeight: 900, color: "#d6e6ff", fontSize: "1rem" }}>
                Token history
              </div>
              <div style={{ fontSize: "0.85rem", opacity: 0.75, marginTop: 2 }}>
                Spends, purchases, and subscription credits · your local time
              </div>
            </div>
            <span
              style={{
                fontWeight: 900,
                fontSize: "1.1rem",
                color: "#ffe066",
                minWidth: 28,
                textAlign: "center",
              }}
            >
              {ledgerOpen ? "▾" : "▸"}
            </span>
          </button>

          {ledgerOpen && (
            <div style={{ marginTop: 12 }}>
              {ledgerLoading && (
                <div style={{ opacity: 0.75, fontSize: "0.95rem" }}>Loading history…</div>
              )}
              {!ledgerLoading && ledgerEntries.length === 0 && (
                <div style={{ opacity: 0.75, fontSize: "0.95rem" }}>
                  No token movements recorded yet.
                </div>
              )}
              {!ledgerLoading &&
                ledgerWithBalances.map((row) => {
                  const qty = Number(row.quantity) || 0;
                  const qtyColor = qty > 0 ? "#9effa8" : qty < 0 ? "#ff9b9b" : "#f5f8ff";
                  const detailBits = [
                    row.gameTitle || row.gameId || null,
                    row.invoiceId ? `Invoice ${row.invoiceId}` : null,
                    row.note || null,
                  ].filter(Boolean);

                  return (
                    <div
                      key={row.id}
                      style={{
                        borderTop: "1px solid rgba(255,255,255,0.10)",
                        padding: "10px 0",
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 8,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 800, fontSize: "0.98rem" }}>
                          {describeLedgerSource(row.source)}
                        </div>
                        <div style={{ fontSize: "0.85rem", opacity: 0.8, marginTop: 2 }}>
                          {fmt(row.ts)}
                        </div>
                        {detailBits.length > 0 && (
                          <div
                            style={{
                              fontSize: "0.82rem",
                              opacity: 0.7,
                              marginTop: 4,
                              lineHeight: 1.35,
                            }}
                          >
                            {detailBits.join(" · ")}
                          </div>
                        )}
                      </div>
                      <div style={{ alignSelf: "start", textAlign: "right", minWidth: 72 }}>
                        <div style={{ fontWeight: 1000, fontSize: "1.15rem", color: qtyColor }}>
                          {formatLedgerQuantity(qty)}
                        </div>
                        <div
                          style={{
                            marginTop: 2,
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            opacity: 0.85,
                            color: "#d6e6ff",
                          }}
                        >
                          Bal {row._balanceAfter}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
